import { random } from 'remotion';

// Types
import type {
  ConfettiRandomPhysics,
  ConfettiParticle,
  ConfettiConfig,
  CanvasSize,
  ConfettiColor,
} from './types';

// Constants
import { PHYSICS, WOBBLE, TILT, FALLBACK } from './constants';

// Utils
import { getConfigValue, toPositiveInt, randomInt, colorsToRgb } from './utils';

const getArrayElement = <T>(array: T[], index: number, fallback: T): T =>
  array[index % array.length] ?? fallback;

/**
 * Creates a confetti particle with randomized physics properties
 */
const createParticle = (opts: ConfettiRandomPhysics): ConfettiParticle => {
  const radAngle = opts.angle * PHYSICS.DEG_TO_RAD;
  const radSpread = opts.spread * PHYSICS.DEG_TO_RAD;
  const seed = String(opts.i);

  return {
    x: opts.x,
    y: opts.y,
    wobble: random(`wobble${seed}`) * WOBBLE.AMPLITUDE,
    wobbleSpeed: Math.min(
      WOBBLE.SPEED_MAX,
      random(`wobbleSpeed${seed}`) * WOBBLE.SPEED_RANGE + WOBBLE.SPEED_MIN,
    ),
    velocity:
      opts.startVelocity * 0.5 + random(`velocity${seed}`) * opts.startVelocity,
    angle2D:
      -radAngle + (0.5 * radSpread - random(`angle2D${seed}`) * radSpread),
    tiltAngle:
      (random(`tiltAngle${seed}`) * TILT.ANGLE_RANGE + TILT.ANGLE_MIN) *
      Math.PI,
    color: opts.color,
    shape: opts.shape,
    tick: 0,
    totalTicks: opts.ticks,
    decay: opts.decay,
    drift: opts.drift,
    random: random(`random${seed}`) + PHYSICS.RANDOM_OFFSET_BASE,
    tiltSin: 0,
    tiltCos: 0,
    wobbleX: 0,
    wobbleY: 0,
    gravity: opts.gravity * PHYSICS.GRAVITY_CONSTANT,
    ovalScalar: PHYSICS.OVAL_SCALAR,
    scalar: opts.scalar,
  };
};

/**
 * Updates particle physics and renders it to the canvas
 */
const updateParticle = (
  ctx: CanvasRenderingContext2D,
  particle: ConfettiParticle,
  tick: number,
): boolean => {
  // Don't render if beyond total ticks
  if (tick >= particle.totalTicks) {
    return false;
  }

  const wobble = particle.wobble + particle.wobbleSpeed * tick;

  // Calculate position based on accumulated physics
  let velocity = particle.velocity;
  let x = particle.x;
  let y = particle.y;

  for (let i = 0; i < tick; i++) {
    velocity *= particle.decay;
    x += Math.cos(particle.angle2D) * velocity + particle.drift;
    y += Math.sin(particle.angle2D) * velocity + particle.gravity;
  }

  // Update tilt and wobble
  const tiltAngle = TILT.SPEED * tick;
  const tiltSin = Math.sin(tiltAngle);
  const tiltCos = Math.cos(tiltAngle);
  const randomValue = random(tick) + PHYSICS.RANDOM_OFFSET_BASE;
  const wobbleX = x + WOBBLE.AMPLITUDE * particle.scalar * Math.cos(wobble);
  const wobbleY = y + WOBBLE.AMPLITUDE * particle.scalar * Math.sin(wobble);

  // Calculate corner positions
  const x1 = x + randomValue * tiltCos;
  const y1 = y + randomValue * tiltSin;
  const x2 = wobbleX + randomValue * tiltCos;
  const y2 = wobbleY + randomValue * tiltSin;

  const { r, g, b } = particle.color;
  ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 1)`;
  ctx.beginPath();

  if (particle.shape === 'circle') {
    ctx.ellipse(
      x,
      y,
      Math.abs(x2 - x1) * particle.ovalScalar,
      Math.abs(y2 - y1) * particle.ovalScalar,
      PHYSICS.ELLIPSE_ROTATION_FACTOR * wobble,
      0,
      2 * Math.PI,
    );
  } else {
    ctx.moveTo(Math.floor(x), Math.floor(y));
    ctx.lineTo(Math.floor(wobbleX), Math.floor(y1));
    ctx.lineTo(Math.floor(x2), Math.floor(y2));
    ctx.lineTo(Math.floor(x1), Math.floor(wobbleY));
  }

  ctx.closePath();
  ctx.fill();

  return true;
};

/**
 * Creates and manages the confetti animation loop
 */
const createAnimation = (
  canvas: HTMLCanvasElement,
  particles: ConfettiParticle[],
  size: CanvasSize,
) => {
  const ctx = canvas.getContext('2d');

  const update = (frame: number): void => {
    if (!ctx) return;

    const { width, height } = size.width && size.height ? size : canvas;
    ctx.clearRect(0, 0, width, height);

    // Convert video frame to internal tick (accounting for fps difference)
    const tick = frame * PHYSICS.TICK_MULTIPLIER;

    for (const particle of particles) {
      updateParticle(ctx, particle, tick);
    }
  };

  return { canvas, update };
};

interface ConfettiCannon {
  fire: (options: ConfettiConfig) => void;
  frame: (currentFrame: number) => void;
}

/**
 * Creates a confetti cannon instance bound to a canvas element
 */
export const confettiCannon = (canvas: HTMLCanvasElement): ConfettiCannon => {
  let animation: ReturnType<typeof createAnimation> | null = null;

  const fire = (options: ConfettiConfig): void => {
    const particleCount = toPositiveInt(
      getConfigValue(options, 'particleCount'),
    );
    const angle = getConfigValue(options, 'angle');
    const spread = getConfigValue(options, 'spread');
    const startVelocity = getConfigValue(options, 'startVelocity');
    const decay = getConfigValue(options, 'decay');
    const gravity = getConfigValue(options, 'gravity');
    const drift = getConfigValue(options, 'drift');
    const colors = colorsToRgb(getConfigValue(options, 'colors'));
    const ticks = getConfigValue(options, 'ticks');
    const shapes = getConfigValue(options, 'shapes');
    const scalar = getConfigValue(options, 'scalar');

    // Fallback color for safe array access
    const fallbackColor: ConfettiColor = colors[0] ?? FALLBACK.COLOR;

    const particles: ConfettiParticle[] = Array.from(
      { length: particleCount },
      (_, i) =>
        createParticle({
          x: options.x,
          y: options.y,
          angle,
          spread,
          startVelocity,
          color: getArrayElement(colors, i, fallbackColor),
          shape: getArrayElement(
            shapes,
            randomInt(0, shapes.length),
            FALLBACK.SHAPE,
          ),
          ticks,
          decay,
          gravity,
          drift,
          scalar,
          i,
        }),
    );

    animation = createAnimation(canvas, particles, {
      width: options.width,
      height: options.height,
    });
  };

  const frame = (currentFrame: number): void => {
    animation?.update(currentFrame);
  };

  return { fire, frame };
};
